<template>
  <v-dialog v-model="show" max-width="650">
    <v-card v-if="car" class="rounded" :loading="loading" dark color="#406d59">
      <v-card-title class=" pa-2 pa-md-3">
        <v-icon>mdi-bus</v-icon>
        <span class="font-weight-bold" style="font-family: 'Oswald', sans-serif !important;font-weight: bold !important;">{{ car.number }}</span>
        <v-spacer></v-spacer>
        <v-chip small color="#bfc1ad" @click="activateReviewPopUp(car)">
          <v-avatar left>
            <v-icon small>mdi-pencil</v-icon>
          </v-avatar>
          <span class="font-weight-bold">Review</span>
        </v-chip>
        <ReviewPopUp
          v-if="reviewPopUp"
          v-model="review"
          :car="reviewPopUp.car"
          :driver="reviewPopUp.driver"
          @close="review = false"
        />
      </v-card-title>
      <v-card-subtitle class=" pa-2 pa-md-3 pb-0 pb-md-0">
        <v-row dense>
          <v-col class="font-weight-bold" style="font-family: 'Oswald', sans-serif !important;font-weight: bold !important;">Stop</v-col>
          <v-col class="font-weight-bold text-center" style="font-family: 'Oswald', sans-serif !important;font-weight: bold !important;">Time</v-col>
          <v-col class="font-weight-bold text-center"style="font-family: 'Oswald', sans-serif !important;font-weight: bold !important;">ETA</v-col>
          <v-col class="font-weight-bold text-center"style="font-family: 'Oswald', sans-serif !important;font-weight: bold !important;">Status</v-col>
        </v-row>
      </v-card-subtitle>
      <v-card-text class=" pa-2 pa-md-3 pt-0 pt-md-0">
        <v-row
          v-for="(stop, i) in stops"
          :key="`stop-${i + 1}`"
          dense
          class="align-center"
        >
          <v-col class="">{{ stop.name }}</v-col>
          <v-col class=" text-center">{{ stop.time }}</v-col>
          <v-col class=" text-center">{{ stop.eta }}</v-col>
          <v-col class=" text-center">
            <v-chip v-if="stop.status" small :color="getColor(stop.status)">
              {{ stop.status }}
            </v-chip>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-snackbar v-model="error" bottom color="error" :timeout="10000">
      {{ errorMsg }}
      <v-btn dark text @click="error = false">
        Close
      </v-btn>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import ReviewPopUp from '../components/ReviewPopUp'
import API from '../services/api'
import { storage } from '../services/firebase'
import moment from 'moment'

export default {
  name: 'CarPopUp',
  components: {
    ReviewPopUp
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    car: {
      type: Object,
      default: null
    },
    route: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    loading: false,
    error: false,
    errorMsg: null,
    stops: [],
    reviewPopUp: null,
    review: false,
    etaTimer: null
  }),
  computed: {
    show: {
      get: function() {
        return this.value
      },
      set: function(value) {
        this.$emit('input', value)
      }
    }
  },
  watch: {
    car: function() {
      this.stops = this.setStops()
    },
    stops: function() {
      clearTimeout(this.etaTimer)
      this.etaTimer = setTimeout(() => {
        this.stops = this.setStops()
      }, 3000)
    }
  },
  mounted: function() {
    if (this.car) {
      this.stops = this.setStops()
    }
  },
  methods: {
    setStops: function() {
      let stops = this.route.stops.map(stop => {
        let id = stop.id
        let name = stop.name
        let todaySchedule = this.findTodaySchedule(stop.schedule)
        let upcoming = this.findUpcomingStopTime(todaySchedule)
        let time = todaySchedule[upcoming]
        return { id, name, time }
      })
      if (this.car.eta) {
        // Process stops data
        stops = stops.map(stop => {
          let name = stop.name
          let time = stop.time
          let stopETA = this.car.eta.find(s => stop.id === s.stop)
          let eta = stopETA.arrived
            ? moment(stopETA.arrived.toDate()).format('hh:mm A')
            : stopETA.eta
            ? stopETA.eta >= 0
              ? moment()
                  .add(stopETA.eta, 'seconds')
                  .format('hh:mm A')
              : null
            : null
          let diff =
            time && eta
              ? moment(time, 'hh:mm A').diff(moment(eta, 'hh:mm A'), 'minutes')
              : null
          let status = diff
            ? diff === 0
              ? 'On-Time'
              : diff > 0
              ? 'Early'
              : 'Delayed'
            : time
            ? 'Recalculating'
            : 'Unknown'
          return { name, time, eta, status }
        })
      }
      return stops
    },
    getColor: function(status) {
      switch (status) {
        case 'Active':
          return '#2877B4'
        case 'Delayed':
          return '#E53717'
        case 'On-Time':
          return '#4BB344'
        case 'Early':
          return '#E5B839'
        default:
          return '#757575'
      }
    },
    findTodaySchedule: function(schedules) {
      /**
       * Returns today's schedule from an Object of named schedules
       */
      let today = Object.keys(schedules).findIndex(
        s => s === moment().format('dddd')
      )
      if (today < 0) today = 0
      let todaySchedule = Object.values(schedules)[today]
      return todaySchedule
    },
    findUpcomingStopTime: function(schedule) {
      /**
       * Returns the index of the upcoming stop time in an schedule array
       */
      // Get the time differences for schedule times with today's date
      let diffs = schedule.map(time =>
        moment(time, 'hh:mm A').diff(moment(), 'minutes')
      )
      // Find which one is closer forward in time
      let upcoming = Math.min(...diffs.filter(d => d >= 0))
      return diffs.findIndex(d => d === upcoming)
    },
    activateReviewPopUp: async function(car) {
      try {
        this.error = false
        this.loading = true
        const { data: driver } = await API.getDriver(car.driver)
        if (driver.picture) {
          driver.picture = await storage()
            .ref(`img_users/${driver.picture}`)
            .getDownloadURL()
        }
        const { data: reviews } = await API.getReviewsByDriver(car.driver)
        let rating = 0
        if (reviews.length > 0) {
          reviews.map(review => (rating += review.rate))
          rating = Math.round((rating / reviews.length) * 10) / 10
        } else {
          rating = null
        }
        this.reviewPopUp = {
          car,
          driver: Object.assign(driver, { rating })
        }
        this.review = true
      } catch (error) {
        this.errorMsg = error.message
        this.error = true
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style>
.rounded {
  border-radius: 12px !important;
}
</style>
